import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Settings, Building2, ShoppingCart, TrendingUp, Calendar, Check, FileText, Hash, User, Gauge, Trash2, Route } from 'lucide-react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import type { DealServiceFormData } from '../../lib/types';
import { useClientStore } from '../../lib/stores/clientStore';
import { useServiceStore } from '../../lib/stores/serviceStore';
import { useServiceCommissionStore } from '../../lib/stores/serviceCommissionStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useReferrerStore } from '../../lib/stores/referrerStore';
import { useUserStore } from '../../lib/stores/userStore';
import { useTheme } from '../../contexts/ThemeContext';

interface DealFormHeaderProps {
  currentStep: number;
  formData: any;
  serviceItems: DealServiceFormData[];
  dealReferrerIds: string[];
  activeTab: 'deal' | 'commissions' | 'referrers' | 'billing';
  onTabChange: (tab: 'deal' | 'commissions' | 'referrers' | 'billing') => void;
  onGoToNextStep: () => void;
  onGoToPreviousStep: () => void;
  onNavigateBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  language: string;
  translations: any;
  onServiceSelect?: (index: number) => void;
  onFormDataChange?: (updates: any) => void;
  onRemoveService?: (index: number) => void;
  /** Timestamp ultimo autosave bozza (ms epoch). null = nessun autosave (es. modifica). */
  draftSavedAt?: number | null;
  /** True se siamo in modifica di un deal esistente (no autosave bozza). */
  isEditMode?: boolean;
}

// Indicatore "Bozza salvata Xs fa" — si auto-aggiorna ogni 10s
function AutosaveIndicator({ savedAt, language }: { savedAt: number; language: string }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force(x => x + 1), 10000);
    return () => clearInterval(t);
  }, []);
  const seconds = Math.max(0, Math.floor((Date.now() - savedAt) / 1000));
  let label: string;
  if (seconds < 5) label = language === 'it' ? 'Bozza salvata ora' : 'Draft saved now';
  else if (seconds < 60) label = language === 'it' ? `Bozza salvata ${seconds}s fa` : `Draft saved ${seconds}s ago`;
  else if (seconds < 3600) label = language === 'it' ? `Bozza salvata ${Math.floor(seconds/60)}m fa` : `Draft saved ${Math.floor(seconds/60)}m ago`;
  else label = language === 'it' ? 'Bozza salvata' : 'Draft saved';
  return (
    <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-black/45 dark:text-white/45">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500/70" />
      {label}
    </span>
  );
}

// Badge step con stato - Notion-like, cliccabile per navigare
function StepBadge({ number, label, active, completed, onClick }: {
  number: number;
  label: string;
  active: boolean;
  completed: boolean;
  onClick?: () => void;
}) {
  const isClickable = onClick && !active;
  return (
    <button
      type="button"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
        active
          ? 'bg-black/[0.04] dark:bg-white/[0.08]'
          : completed
            ? 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
            : ''
      } ${isClickable ? 'cursor-pointer hover:bg-black/[0.04] dark:hover:bg-white/[0.06]' : !active ? 'opacity-60' : ''}`}
    >
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold transition-colors ${
        active ? 'bg-[#E91E8C] text-white' :
        completed ? 'bg-green-600 dark:bg-green-500 text-white' :
        'bg-black/10 dark:bg-white/15 text-black/55 dark:text-white/55'
      }`}>
        {completed ? <Check size={11} /> : number}
      </div>
      <span className={`text-[13px] font-medium ${
        active ? 'text-[#E91E8C]' :
        completed ? 'text-black/70 dark:text-white/70' :
        'text-black/45 dark:text-white/45'
      }`}>{label}</span>
    </button>
  );
}

// Card dati con icona
function DataCard({ icon: Icon, label, value }: {
  icon: React.ComponentType<any>;
  label: string;
  value: string;
}) {
  return (
    <div className="glass-card rounded-lg p-2">
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon size={12} className="text-black/60 dark:text-white/60" />
        <span className="text-[10px] text-black/60 dark:text-white/60">{label}</span>
      </div>
      <div className="text-xs font-semibold text-black dark:text-white truncate" title={value}>
        {value || '-'}
      </div>
    </div>
  );
}

// Tooltip component
function Tooltip({ children, text, position = 'center' }: { children: React.ReactNode; text: string; position?: 'center' | 'left' }) {
  const positionClasses = position === 'left'
    ? 'right-0'
    : 'left-1/2 -translate-x-1/2';
  const arrowClasses = position === 'left'
    ? 'right-3'
    : 'left-1/2 -translate-x-1/2';

  return (
    <div className="group relative">
      {children}
      <div className={`absolute bottom-full ${positionClasses} mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50`}>
        {text}
        <div className={`absolute top-full ${arrowClasses} border-4 border-transparent border-t-gray-900 dark:border-t-gray-700`} />
      </div>
    </div>
  );
}

export function DealFormHeader({
  currentStep,
  formData,
  serviceItems,
  dealReferrerIds,
  activeTab,
  onTabChange,
  onGoToNextStep,
  onGoToPreviousStep,
  onNavigateBack,
  onSubmit,
  submitting,
  language,
  translations: t,
  onServiceSelect,
  onFormDataChange,
  onRemoveService,
  draftSavedAt,
  isEditMode
}: DealFormHeaderProps) {
  const { clients } = useClientStore();
  const { services } = useServiceStore();
  const { userRates, referrerRates } = useServiceCommissionStore();
  const { settings } = useSettingsStore();
  const { referrers } = useReferrerStore();
  const { users } = useUserStore();
  const { isDark } = useTheme();

  // State for expand/collapse - default compressed
  const [isExpanded, setIsExpanded] = useState(false);

  // State for offer_path expansion
  const [isOfferPathExpanded, setIsOfferPathExpanded] = useState(false);

  // User options for searchable select
  const userOptions = useMemo(() => {
    return users.map(user => ({
      value: user.id,
      label: user.full_name || user.email || 'Utente'
    }));
  }, [users]);

  // Custom styles for the compact user select
  const compactSelectStyles = {
    container: (base: any) => ({
      ...base,
      minWidth: '120px'
    }),
    control: (base: any) => ({
      ...base,
      minHeight: 'unset',
      height: 'auto',
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      cursor: 'pointer',
      flexWrap: 'nowrap' as const,
      '&:hover': {
        border: 'none'
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: 0,
      flexWrap: 'nowrap' as const
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? 'white' : 'black',
      fontSize: '12px',
      margin: 0,
      maxWidth: '100px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: isDark ? 'white' : 'black',
      fontSize: '12px'
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
      fontSize: '12px'
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: 'auto',
      alignSelf: 'center'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 2px',
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
      svg: { width: '14px', height: '14px' }
    }),
    clearIndicator: (base: any) => ({
      ...base,
      padding: '0 2px',
      svg: { width: '12px', height: '12px' }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menu: (base: any) => ({
      ...base,
      background: isDark ? 'rgba(30, 30, 30, 0.95)' : 'white',
      backdropFilter: 'blur(16px)',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: '0.5rem',
      zIndex: 9999,
      minWidth: '280px',
      right: 0
    }),
    menuList: (base: any) => ({
      ...base,
      padding: '0.15rem',
      maxHeight: '180px'
    }),
    option: (base: any, state: any) => ({
      ...base,
      background: state.isFocused
        ? (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.05)')
        : 'transparent',
      color: isDark ? 'white' : 'black',
      fontSize: '12px',
      padding: '0.3rem 0.75rem',
      borderRadius: '0.25rem',
      cursor: 'pointer'
    })
  };

  // Format number with Italian locale (dot for thousands, comma for decimals)
  const formatCurrency = (value: number) => {
    return value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate total amount (services only, without variables)
  const totalAmount = useMemo(() => {
    let total = 0;

    serviceItems.forEach(service => {
      const baseAmount = service.discounted_amount || service.amount || 0;
      const isMonthlyService = services.find(s => s.id === service.service_id)?.service_type === 'monthly';

      let serviceTotal = baseAmount;
      if (isMonthlyService && service.monthly_duration && service.monthly_duration > 0) {
        serviceTotal = baseAmount * service.monthly_duration;
      }

      total += serviceTotal;
    });

    return total;
  }, [serviceItems, services]);

  // Calculate commissions (simplified from FloatingActionBar)
  const totalCommission = useMemo(() => {
    let totalUserCommission = 0;

    serviceItems.forEach(service => {
      const serviceAmount = service.discounted_amount || service.amount || 0;
      const finalAmount = (service.monthly_duration && service.monthly_duration > 0) ? serviceAmount * service.monthly_duration : serviceAmount;

      // Get user commission rate
      const userId = formData.assigned_to;
      if (userId) {
        const serviceUserRate = userRates.find(
          r => r.service_id === service.service_id && r.user_id === userId && r.is_active
        );
        const rate = serviceUserRate ? serviceUserRate.commission_rate : (settings?.default_commission_rate || 10.0);

        if (service.payment_schedule?.installments && service.payment_schedule.installments.length > 0) {
          service.payment_schedule.installments.forEach((installment) => {
            const installmentAmount = installment.amount || installment.value || 0;
            totalUserCommission += installmentAmount * (rate / 100);
          });
        } else {
          totalUserCommission += finalAmount * (rate / 100);
        }
      }
    });

    return totalUserCommission;
  }, [serviceItems, formData.assigned_to, userRates, settings]);

  // Le righe prodotto non hanno un metodo di pagamento proprio: la fatturazione
  // le emette come saldo unico, e il metodo con cui quel saldo va incassato si
  // sceglie nel tab Fatturazione. Qui serve solo a sapere se quella scelta
  // manca, per segnalarlo sul tab: su un'offerta di soli servizi non succede
  // nulla di tutto cio'.
  const hasProductLines = useMemo(
    () => serviceItems.some(s => (s as any).line_type === 'product'),
    [serviceItems]
  );
  const missingProductPayment = currentStep === 2 && hasProductLines && !formData?.payment_method_id;

  // Get client name
  const clientName = useMemo(() => {
    if (!formData?.client_id) return '-';
    const client = clients.find(c => c.id === formData.client_id);
    if (!client) return '-';
    return client.company_name || client.full_name || '-';
  }, [formData?.client_id, clients]);

  // Format date
  const dealDate = useMemo(() => {
    if (!formData?.deal_date) return '-';
    try {
      const date = new Date(formData.deal_date);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('it-IT');
    } catch {
      return '-';
    }
  }, [formData?.deal_date]);

  return (
    <div className="sticky top-0 z-40 pl-10 pr-3 py-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm">
      {/* Step Indicator con Navigation Buttons - Riga 1 */}
      <div className="flex items-center justify-between pb-2 border-b border-black/[0.06] dark:border-white/10">
        <div className="flex items-center gap-2">
          {/* Back button - only for step navigation (step 1 close is handled by drawer X) */}
          {currentStep > 1 && (
            <button
              type="button"
              onClick={onGoToPreviousStep}
              className="p-1.5 hover:bg-black/10 dark:hover:bg-white/10 text-black dark:text-white rounded-lg transition-colors"
              aria-label={language === 'it' ? 'Indietro' : 'Back'}
            >
              <ChevronLeft size={16} />
            </button>
          )}

          <StepBadge
            number={1}
            label={language === 'it' ? 'Info Generali' : 'General Info'}
            active={currentStep === 1}
            completed={currentStep > 1}
            onClick={currentStep === 2 ? onGoToPreviousStep : undefined}
          />
          <ChevronRight className="text-black/40 dark:text-white/40" size={16} />
          <StepBadge
            number={2}
            label={language === 'it' ? 'Servizi' : 'Services'}
            active={currentStep === 2}
            completed={false}
            onClick={currentStep === 1 ? onGoToNextStep : undefined}
          />

          {/* Expand/Collapse button */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-black/10 dark:hover:bg-white/10 text-black dark:text-white rounded-lg transition-colors ml-1"
            aria-label={isExpanded ? (language === 'it' ? 'Comprimi' : 'Collapse') : (language === 'it' ? 'Espandi' : 'Expand')}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {/* Navigation buttons on the right */}
        <div className="flex items-center gap-2">
          {/* Indicatore autosave bozza — visibile solo in CREAZIONE (no edit) */}
          {!isEditMode && draftSavedAt && (
            <AutosaveIndicator savedAt={draftSavedAt} language={language} />
          )}

          {/* Carrellino rimosso: l'elenco servizi/prodotti aggiunti e' ora
              una "scheda" allineata a destra dentro EnhancedDealServiceSelector
              (cliccabile per accedere alla lista compatta). */}

          {currentStep === 1 && (
            <button
              type="button"
              onClick={onGoToNextStep}
              className="inline-flex items-center gap-1.5 px-3 h-8 text-[13px] font-medium rounded-md bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 transition-colors"
            >
              <span>{language === 'it' ? 'Avanti' : 'Next'}</span>
              <ChevronRight size={16} />
            </button>
          )}

          {currentStep === 2 && (
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-3 h-8 text-[13px] font-medium rounded-md bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <span className="w-3 h-3 border-2 border-white/40 border-t-white dark:border-gray-900/40 dark:border-t-gray-900 rounded-full animate-spin" />
                  <span>{language === 'it' ? 'Salvataggio…' : 'Saving…'}</span>
                </>
              ) : (
                <span>{language === 'it' ? 'Salva Offerta' : 'Save Deal'}</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Riepilogo Dati - Riga 2 (grid responsive) - Conditional visibility based on isExpanded */}
      {isExpanded && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 mt-2">
          <DataCard
            icon={Building2}
            label={language === 'it' ? 'Cliente' : 'Client'}
            value={clientName}
          />
          <DataCard
            icon={ShoppingCart}
            label={language === 'it' ? 'Servizi' : 'Services'}
            value={serviceItems.length > 0 ? `${serviceItems.length} (€${totalAmount.toFixed(2)})` : '-'}
          />
          <DataCard
            icon={TrendingUp}
            label={language === 'it' ? 'Commissioni' : 'Commissions'}
            value={totalCommission > 0 ? `€${totalCommission.toFixed(2)}` : '-'}
          />
          <DataCard
            icon={Calendar}
            label={language === 'it' ? 'Data' : 'Date'}
            value={dealDate}
          />
        </div>
      )}


      {/* Tab Offerta/Commissioni - Riga 3 (sempre visibili) */}
      <div className={`flex justify-between items-center border-b border-black/10 dark:border-white/10 -mb-2 ${isExpanded ? '' : 'mt-2'}`}>
          <div className="flex">
            <button
              onClick={() => onTabChange('deal')}
              className={`flex items-center gap-1.5 px-4 py-2 font-medium text-sm transition-colors relative ${
                activeTab === 'deal'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white'
              }`}
            >
              <FileText size={14} />
              {language === 'it' ? 'Offerta' : 'Deal'}
              {activeTab === 'deal' && (
                <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#E91E8C] dark:bg-[#E91E8C]" />
              )}
            </button>

            <button
              onClick={() => onTabChange('commissions')}
              className={`flex items-center gap-1.5 px-4 py-2 font-medium text-sm transition-colors relative ${
                activeTab === 'commissions'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white'
              }`}
            >
              <Settings size={14} />
              {language === 'it' ? 'Commissioni' : 'Commissions'}
              {activeTab === 'commissions' && (
                <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#E91E8C] dark:bg-[#E91E8C]" />
              )}
            </button>

            <button
              onClick={() => onTabChange('billing')}
              className={`flex items-center gap-1.5 px-4 py-2 font-medium text-sm transition-colors relative ${
                activeTab === 'billing'
                  ? 'text-gray-900 dark:text-white'
                  : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white'
              }`}
            >
              <FileText size={14} />
              {language === 'it' ? 'Fatturazione' : 'Billing'}
              {/* L'offerta ha prodotti ma nessun metodo di pagamento: la scelta
                  vive qui dentro, e senza un segnale si arriverebbe al salvataggio
                  senza essersene accorti. */}
              {missingProductPayment && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-amber-500"
                  title={language === 'it'
                    ? 'Manca il metodo di pagamento delle righe prodotto'
                    : 'Product lines payment method is missing'}
                />
              )}
              {activeTab === 'billing' && (
                <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#E91E8C] dark:bg-[#E91E8C]" />
              )}
            </button>
          </div>

          {/* Campi editabili a destra */}
          <div className="flex items-center gap-3">
            {/* Percorso Offerta */}
            <div className="relative">
              <Tooltip text={language === 'it' ? 'Clicca per espandere' : 'Click to expand'}>
                <div
                  className="flex items-center gap-1.5 cursor-pointer"
                  onClick={() => setIsOfferPathExpanded(true)}
                >
                  <Route size={14} className="text-black/60 dark:text-white/60" />
                  <span
                    className="text-xs text-black dark:text-white py-1 w-16 truncate"
                    title={formData?.offer_path || ''}
                  >
                    {formData?.offer_path || (
                      <span className="text-gray-400 dark:text-gray-500">
                        {language === 'it' ? 'Percorso' : 'Path'}
                      </span>
                    )}
                  </span>
                </div>
              </Tooltip>

              {/* Popup espanso */}
              {isOfferPathExpanded && (
                <>
                  {/* Overlay per chiudere cliccando fuori */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOfferPathExpanded(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 min-w-[300px]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {language === 'it' ? 'Percorso offerta' : 'Offer path'}
                      </span>
                      <button
                        onClick={() => setIsOfferPathExpanded(false)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <ChevronUp size={14} />
                      </button>
                    </div>
                    <textarea
                      className="w-full text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-2 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={4}
                      value={formData?.offer_path || ''}
                      onChange={(e) => onFormDataChange?.({ offer_path: e.target.value })}
                      placeholder={language === 'it' ? 'Inserisci il percorso completo...' : 'Enter full path...'}
                      autoFocus
                    />
                  </div>
                </>
              )}
            </div>

            {/* Separatore */}
            <div className="h-4 w-px bg-black/10 dark:bg-white/20" />

            {/* Riferimento Offerta */}
            <Tooltip text={language === 'it' ? 'Riferimento interno dell\'offerta' : 'Internal offer reference'}>
              <div className="flex items-center gap-1.5">
                <FileText size={14} className="text-black/60 dark:text-white/60" />
                <input
                  type="text"
                  className="text-xs bg-transparent border-none text-black dark:text-white focus:outline-none focus:ring-0 w-12 focus:w-40 py-1 transition-[width] duration-200 ease-out"
                  value={formData?.offer_reference || ''}
                  onChange={(e) => onFormDataChange?.({ offer_reference: e.target.value })}
                  placeholder={language === 'it' ? 'Rif.' : 'Ref.'}
                />
              </div>
            </Tooltip>

            {/* Separatore */}
            <div className="h-4 w-px bg-black/10 dark:bg-white/20" />

            {/* Utente Assegnato — Select cercabile, esclude superadmin */}
            <Tooltip text={language === 'it' ? 'Utente assegnato all\'offerta' : 'User assigned to the deal'}>
              <div className="flex items-center gap-1 min-w-[160px]">
                <User size={14} className="text-black/60 dark:text-white/60 flex-shrink-0" />
                {(() => {
                  const userOptions = users
                    .filter(u => !(u as any).is_super_admin)
                    .map(u => ({
                      value: u.id,
                      label: u.full_name || u.email || 'Utente',
                    }));
                  const currentValue = userOptions.find(o => o.value === formData?.assigned_to) || null;
                  return (
                    <Select
                      options={userOptions}
                      value={currentValue}
                      onChange={(opt: any) => onFormDataChange?.({ assigned_to: opt?.value || '' })}
                      placeholder={language === 'it' ? 'Seleziona...' : 'Select...'}
                      isSearchable
                      isClearable
                      classNamePrefix="user-select"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      styles={{
                        control: (base: any, state: any) => ({
                          ...base,
                          minHeight: '24px',
                          height: '24px',
                          background: 'transparent',
                          border: 'none',
                          boxShadow: 'none',
                          cursor: 'pointer',
                          minWidth: '140px',
                          fontSize: '12px',
                          '&:hover': { background: state.isFocused ? 'transparent' : 'transparent' },
                        }),
                        valueContainer: (base: any) => ({ ...base, padding: '0 4px', height: '24px' }),
                        indicatorsContainer: (base: any) => ({ ...base, height: '24px' }),
                        dropdownIndicator: (base: any) => ({ ...base, padding: '0 4px' }),
                        clearIndicator: (base: any) => ({ ...base, padding: '0 2px' }),
                        indicatorSeparator: () => ({ display: 'none' }),
                        input: (base: any) => ({ ...base, margin: 0, padding: 0, fontSize: '12px' }),
                        placeholder: (base: any) => ({
                          ...base,
                          color: 'rgba(0,0,0,0.4)',
                          fontSize: '12px',
                        }),
                        singleValue: (base: any) => ({
                          ...base,
                          color: isDark ? 'white' : 'black',
                          fontSize: '12px',
                        }),
                        menu: (base: any) => ({
                          ...base,
                          background: isDark ? 'rgba(30, 30, 30, 0.98)' : 'white',
                          border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                          borderRadius: '8px',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
                          marginTop: '4px',
                          overflow: 'hidden',
                          minWidth: '220px',
                        }),
                        menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                        menuList: (base: any) => ({ ...base, padding: '4px' }),
                        option: (base: any, state: any) => ({
                          ...base,
                          background: state.isFocused
                            ? (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
                            : 'transparent',
                          color: isDark ? 'white' : '#1a1a1a',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  );
                })()}
              </div>
            </Tooltip>

            {/* Separatore */}
            <div className="h-4 w-px bg-black/10 dark:bg-white/20" />

            {/* Data Offerta — spostata prima del numero preventivo cosi'
                il calendario popup non finisce fuori schermo a destra
                (l'header e' allineato a destra: l'input piu' a destra
                aveva il popup tagliato). */}
            <Tooltip text={language === 'it' ? 'Data di creazione dell\'offerta' : 'Deal creation date'}>
              <div className="flex items-center gap-1.5">
                <Calendar size={14} className="text-black/60 dark:text-white/60" />
                <DatePicker
                  selected={formData?.created_at ? new Date(formData.created_at) : new Date()}
                  onChange={(date: Date | null) => {
                    if (date) {
                      onFormDataChange?.({ created_at: date });
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="text-xs bg-transparent border-none text-black dark:text-white focus:outline-none focus:ring-0 w-20 py-1 cursor-pointer"
                  portalId="root-portal"
                />
              </div>
            </Tooltip>

            {/* Separatore */}
            <div className="h-4 w-px bg-black/10 dark:bg-white/20" />

            {/* Numero Offerta */}
            <Tooltip text={language === 'it' ? 'Numero identificativo dell\'offerta' : 'Deal identification number'}>
              <div className="flex items-center gap-1.5">
                <Hash size={14} className="text-black/60 dark:text-white/60" />
                <input
                  type="text"
                  className="text-xs bg-transparent border-none text-black dark:text-white focus:outline-none focus:ring-0 w-24 py-1"
                  value={formData?.quote_number || ''}
                  onChange={(e) => onFormDataChange?.({ quote_number: e.target.value })}
                  placeholder="001/2025"
                />
              </div>
            </Tooltip>

            {/* Separatore */}
            <div className="h-4 w-px bg-black/10 dark:bg-white/20" />

            {/* Probabilità Chiusura */}
            <Tooltip text={language === 'it' ? (settings?.use_simplified_probability ? 'Probabilità di chiusura' : 'Probabilità di chiusura (1-10)') : (settings?.use_simplified_probability ? 'Closing probability' : 'Closing probability (1-10)')} position="left">
              <div className="flex items-center gap-1.5">
                <Gauge size={14} className="text-black/60 dark:text-white/60" />
                <select
                  className="text-xs bg-transparent border-none text-black dark:text-white focus:outline-none focus:ring-0 cursor-pointer py-1 pr-5 appearance-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")', backgroundPosition: 'right 0 center', backgroundRepeat: 'no-repeat', backgroundSize: '1em 1em' }}
                  value={formData?.closing_probability || 5}
                  onChange={(e) => onFormDataChange?.({ closing_probability: parseInt(e.target.value) })}
                >
                {settings?.use_simplified_probability
                  ? [
                      { value: 3, label: '25%' },
                      { value: 5, label: '50%' },
                      { value: 8, label: '75%' }
                    ].map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))
                  : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))
                }
              </select>
              </div>
            </Tooltip>
          </div>
      </div>
    </div>
  );
}
